import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  ArrowLeft,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CirclePlay,
  Eye,
  GraduationCap,
  Search,
  Sparkles,
  X,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import { useHelpCenter } from '../contexts/HelpCenterContext';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '@/lib/utils';
import { API_URL } from '@/apiBase';
import { mergeTutorialFullView } from '@/lib/helpTutorialStorage';
import { StepPositionedBubbles, toYoutubeEmbed, resolveHelpMediaUrl } from './help/HelpTutorialShared';

function TutorialBadges({ nouveau, misAjour }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {nouveau && (
        <Badge variant="default" className="bg-emerald-600 text-white hover:bg-emerald-600 text-[10px] px-1.5 py-0">
          Nouveau
        </Badge>
      )}
      {misAjour && (
        <Badge variant="secondary" className="bg-amber-500/90 text-white hover:bg-amber-500/90 text-[10px] px-1.5 py-0">
          Mis à jour
        </Badge>
      )}
    </div>
  );
}

/** Au moins un tutoriel « Nouveau » ou « Mis à jour » → pastille possible sur l’en-tête du thème (si replié). */
function themeHasNouveauOuMiseAJour(theme) {
  return theme.tutorials?.some((t) => t.badge_nouveau || t.badge_mis_a_jour) ?? false;
}

function filterThemesByTutorialSearch(themes, needle) {
  const q = needle.trim().toLowerCase();
  if (!q || !themes?.length) return themes || [];
  return themes
    .map((th) => ({
      ...th,
      tutorials: (th.tutorials || []).filter((t) => {
        const titre = String(t.titre ?? '').toLowerCase();
        const desc = String(t.description ?? '').toLowerCase();
        return titre.includes(q) || desc.includes(q);
      }),
    }))
    .filter((th) => th.tutorials.length > 0);
}

const HelpCenterPanel = () => {
  const { open, setOpen, refreshSummary } = useHelpCenter();
  const { user } = useAuth();
  const [catalog, setCatalog] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [view, setView] = useState('list');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [completePending, setCompletePending] = useState(false);
  /** null = pas encore hydraté depuis le catalogue ; ids des thèmes développés (vide = tout fermé à l’ouverture) */
  const [accordionOpen, setAccordionOpen] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/help-center/catalog`);
      setCatalog(data);
    } catch {
      setCatalog(null);
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadCatalog();
      setView('list');
      setDetail(null);
      setStepIndex(0);
    }
  }, [open, loadCatalog]);

  useEffect(() => {
    if (!open) {
      setSearchQuery('');
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, setOpen]);

  useLayoutEffect(() => {
    if (!open) {
      setAccordionOpen(null);
      return;
    }
    if (!catalog?.themes?.length) {
      return;
    }
    setAccordionOpen((prev) => {
      if (prev === null) {
        return [];
      }
      const valid = new Set(catalog.themes.map((t) => t.id));
      return prev.filter((id) => valid.has(id));
    });
  }, [open, catalog]);

  const searchNeedle = searchQuery.trim();
  const isSearchActive = searchNeedle.length > 0;

  const filteredThemes = useMemo(
    () => filterThemesByTutorialSearch(catalog?.themes, searchNeedle),
    [catalog?.themes, searchNeedle],
  );

  useEffect(() => {
    setAccordionOpen((prev) => {
      if (prev === null) return prev;
      const valid = new Set(filteredThemes.map((t) => t.id));
      return prev.filter((id) => valid.has(id));
    });
  }, [filteredThemes]);

  const openTutorial = async (tutorialId) => {
    setDetailLoading(true);
    setView('detail');
    setStepIndex(0);
    try {
      const { data } = await axios.get(`${API_URL}/api/help-center/tutorials/${tutorialId}`);
      setDetail(data.tutorial);
    } catch {
      setDetail(null);
      setView('list');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!detail?.id) return;
    setCompletePending(true);
    try {
      await axios.post(`${API_URL}/api/help-center/tutorials/${detail.id}/complete`);
      if (user?.id) {
        mergeTutorialFullView(user.id, detail.id);
      }
      await refreshSummary();
      await loadCatalog();
      setDetail((d) => (d ? { ...d, completed: true, badge_nouveau: false, badge_mis_a_jour: false } : d));
    } finally {
      setCompletePending(false);
    }
  };

  const backToList = () => {
    setView('list');
    setDetail(null);
    loadCatalog();
    refreshSummary();
  };

  const etapes = detail?.etapes || [];
  const isEtapes = detail?.mode === 'etapes' && etapes.length > 0;
  const lastStep = isEtapes && stepIndex >= etapes.length - 1;
  const currentStepScreenshot = isEtapes && etapes[stepIndex]
    ? resolveHelpMediaUrl(etapes[stepIndex].screenshot_url)
    : null;
  const embed = detail?.video_url ? toYoutubeEmbed(detail.video_url) : null;
  const hasFullViewUrl = Boolean(
    detail?.full_view_url && String(detail.full_view_url).trim(),
  );

  const openTutorialExpandedWindow = () => {
    if (!detail?.id) return;
    const external = typeof detail.full_view_url === 'string' ? detail.full_view_url.trim() : '';
    if (!external) return;
    window.open(external, '_blank', 'noopener,noreferrer');
  };

  const onParcoursTermineClick = async () => {
    if (completePending) return;
    if (!detail.completed) {
      await handleComplete();
    } else {
      backToList();
    }
  };

  const onMarkSeenClick = async () => {
    if (completePending) return;
    if (!detail.completed) {
      await handleComplete();
    } else {
      backToList();
    }
  };

  return (
    <aside
      id="help-center-panel"
      aria-hidden={!open}
      className={cn(
        'relative flex h-full shrink-0 flex-col overflow-hidden bg-background transition-[width,opacity] duration-300 ease-out',
        open
          ? 'min-w-[300px] w-[max(300px,min(25vw,540px))] max-w-full rounded-t-2xl border-l border-border opacity-100 shadow-xl'
          : 'pointer-events-none w-0 min-w-0 max-w-0 border-0 opacity-0',
      )}
    >
      <button
        type="button"
        onClick={() => setOpen(false)}
        className={cn(
          'absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-xl border border-border',
          'text-muted-foreground transition-colors',
          'hover:border-muted-foreground/50 hover:bg-accent hover:text-foreground',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
          (view !== 'list' || !open) && 'hidden',
        )}
        aria-label="Fermer le centre d'aide"
      >
        <X className="h-[18px] w-[18px]" strokeWidth={2} aria-hidden />
      </button>
      <div className="relative flex min-h-0 min-w-0 flex-1 flex-col p-0">
        {view === 'list' ? (
          <>
            <header className="shrink-0 flex flex-col border-b border-border bg-background pt-6 pb-2 pl-6 pr-14">
              <div className="flex items-start gap-3">
                <GraduationCap
                  className="h-5 w-5 shrink-0 text-muted-foreground"
                  strokeWidth={1.5}
                  aria-hidden
                />
                <div className="flex min-w-0 flex-col space-y-1">
                  <h2 className="text-left text-lg font-semibold leading-snug text-foreground">
                    Centre d&apos;aide
                  </h2>
                  <p className="text-left text-sm font-normal leading-normal text-muted-foreground">
                    {catalog?.application?.name || 'Annuaire Interne'}
                  </p>
                </div>
              </div>
            </header>

            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <div className="min-h-0 flex-1 overflow-y-auto">
                {!catalogLoading && catalog?.themes?.length > 0 && !catalog?.empty && (
                  <div className="sticky top-0 z-[1] shrink-0 border-b border-border bg-background px-6 py-3">
                    <label htmlFor="help-center-tutorial-search" className="sr-only">
                      Rechercher un tutoriel par titre ou description
                    </label>
                    <div className="relative">
                      <Search
                        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                        strokeWidth={2}
                        aria-hidden
                      />
                      <Input
                        id="help-center-tutorial-search"
                        type="search"
                        autoComplete="off"
                        placeholder="Rechercher un tutoriel…"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="h-9 border-border bg-background pl-9 text-sm text-foreground placeholder:text-muted-foreground"
                      />
                    </div>
                  </div>
                )}

                <div className="px-6 py-4">
                  {catalogLoading && <p className="text-sm text-muted-foreground">Chargement…</p>}
                  {!catalogLoading && catalog?.empty && (
                    <div className="rounded-lg border border-dashed border-border bg-muted/30 p-6 text-center">
                      <p className="text-sm font-medium text-foreground">Aucun tutoriel pour cette application</p>
                      <p className="text-xs text-muted-foreground mt-2">
                        Lorsque des thèmes et tutoriels seront publiés dans l&apos;admin Prysma pour{' '}
                        <strong>{catalog?.application?.name}</strong>, ils apparaîtront ici.
                      </p>
                    </div>
                  )}
                  {!catalogLoading &&
                    !catalog?.empty &&
                    catalog?.themes?.length > 0 &&
                    isSearchActive &&
                    filteredThemes.length === 0 && (
                      <p className="py-8 text-center text-sm text-muted-foreground">
                        <strong>Aucun tutoriel ne correspond à votre recherche.</strong>
                      </p>
                    )}
                  {!catalogLoading &&
                    !catalog?.empty &&
                    catalog?.themes?.length > 0 &&
                    accordionOpen !== null &&
                    filteredThemes.length > 0 && (
                      <Accordion
                        type="multiple"
                        value={accordionOpen}
                        onValueChange={setAccordionOpen}
                        className="w-full"
                      >
                        {filteredThemes.map((theme) => {
                          const themeExpanded = accordionOpen.includes(theme.id);
                          return (
                            <AccordionItem value={theme.id} key={theme.id} className="w-full">
                              <AccordionTrigger
                                hideChevron
                                className="w-full gap-2 bg-transparent py-4 text-left shadow-none hover:no-underline [&[data-state=open]]:no-underline"
                              >
                                <span className="min-w-0 flex-1 truncate text-left text-sm font-medium text-foreground">
                                  {theme.nom}
                                </span>
                                <div className="flex shrink-0 items-center gap-2">
                                  {themeHasNouveauOuMiseAJour(theme) && !themeExpanded && (
                                    <Badge
                                      variant="default"
                                      className="gap-0.5 border-transparent bg-emerald-600 px-1.5 py-0 text-[10px] font-semibold text-white shadow hover:bg-emerald-600"
                                    >
                                      <Sparkles className="h-2.5 w-2.5 shrink-0" strokeWidth={2} aria-hidden />
                                      Nouveau
                                    </Badge>
                                  )}
                                  <span className="text-muted-foreground" aria-hidden>
                                    {themeExpanded ? (
                                      <ChevronUp className="h-4 w-4" strokeWidth={2} />
                                    ) : (
                                      <ChevronDown className="h-4 w-4" strokeWidth={2} />
                                    )}
                                  </span>
                                </div>
                              </AccordionTrigger>
                              <AccordionContent>
                                <ul className="space-y-2 pl-1">
                                  {theme.tutorials.map((tuto) => (
                                    <li key={tuto.id}>
                                      <button
                                        type="button"
                                        onClick={() => openTutorial(tuto.id)}
                                        className={cn(
                                          'w-full text-left rounded-lg border border-border p-3 transition-colors',
                                          'hover:bg-accent/80 focus:outline-none focus:ring-2 focus:ring-ring',
                                        )}
                                      >
                                        <div className="flex justify-between gap-2 items-start">
                                          <div className="min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                              {tuto.mode === 'etapes' ? (
                                                <GraduationCap className="h-4 w-4 shrink-0 text-muted-foreground" />
                                              ) : (
                                                <CirclePlay className="h-4 w-4 shrink-0 text-muted-foreground" />
                                              )}
                                              <span className="font-medium text-sm">{tuto.titre}</span>
                                            </div>
                                            {tuto.description && (
                                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                                {tuto.description}
                                              </p>
                                            )}
                                          </div>
                                          <TutorialBadges nouveau={tuto.badge_nouveau} misAjour={tuto.badge_mis_a_jour} />
                                        </div>
                                        {tuto.completed && (
                                          <p className="text-[11px] text-emerald-600 dark:text-emerald-400 mt-2">
                                            Parcours terminé
                                          </p>
                                        )}
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              </AccordionContent>
                            </AccordionItem>
                          );
                        })}
                      </Accordion>
                    )}
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
            <header className="shrink-0 border-b border-border bg-background px-6 pb-4 pt-6">
              <div className="flex items-center justify-between gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 gap-1 px-2 text-muted-foreground hover:bg-accent hover:text-foreground"
                  onClick={backToList}
                >
                  <ArrowLeft className="h-4 w-4 shrink-0" strokeWidth={2} aria-hidden />
                  Retour
                </Button>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className={cn(
                    'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-border',
                    'text-muted-foreground transition-colors',
                    'hover:border-muted-foreground/50 hover:bg-accent hover:text-foreground',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                  )}
                  aria-label="Fermer le centre d'aide"
                >
                  <X className="h-[18px] w-[18px]" strokeWidth={2} aria-hidden />
                </button>
              </div>
              {!detailLoading && detail && (
                <div className="mt-4 space-y-1">
                  <div className="flex items-start justify-between gap-2">
                    <h2 className="min-w-0 flex-1 truncate text-left text-lg font-semibold leading-snug text-foreground">
                      {detail.titre || 'Tutoriel'}
                    </h2>
                    <div className="flex shrink-0 items-start gap-2">
                      <Eye className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={2} aria-hidden />
                      {detail.completed && (
                        <span className="whitespace-nowrap pt-0.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                          Parcours terminé
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-sm font-normal text-muted-foreground">
                    {detail.theme_nom ? `Thème : ${detail.theme_nom}` : '\u00a0'}
                  </p>
                  {detail.description && (
                    <p className="text-sm font-normal leading-snug text-muted-foreground">{detail.description}</p>
                  )}
                  {(detail.badge_nouveau || detail.badge_mis_a_jour) && (
                    <div className="pt-0.5 text-xs font-normal text-muted-foreground">
                      <TutorialBadges nouveau={detail.badge_nouveau} misAjour={detail.badge_mis_a_jour} />
                    </div>
                  )}
                </div>
              )}
              {detailLoading && (
                <div className="mt-4 space-y-1">
                  <h2 className="text-lg font-semibold text-foreground">Chargement…</h2>
                </div>
              )}
            </header>

            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-4 pt-2">
                {detailLoading && <p className="text-sm text-muted-foreground">Chargement du tutoriel…</p>}

                {!detailLoading && detail && (
                  <div className="space-y-4">
                    {!isEtapes && detail.video_url && (
                      <div className="space-y-2">
                        {embed ? (
                          <div className="aspect-video w-full overflow-hidden rounded-lg bg-black">
                            <iframe
                              title={detail.titre}
                              src={embed}
                              className="h-full w-full"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          </div>
                        ) : (
                          <video
                            src={detail.video_url}
                            controls
                            className="max-h-[50vh] w-full rounded-lg bg-black"
                            onEnded={handleComplete}
                          />
                        )}
                        <Button
                          type="button"
                          className="w-full"
                          variant="default"
                          disabled={completePending}
                          onClick={onMarkSeenClick}
                        >
                          {detail.completed ? 'Retour aux tutoriels' : 'Marquer comme vu'}
                        </Button>
                      </div>
                    )}

                    {!isEtapes && !detail.video_url && (
                      <>
                        <p className="text-sm text-muted-foreground">
                          Aucune vidéo n&apos;est associée à ce tutoriel. Marquez-le comme vu lorsque vous avez terminé la
                          lecture.
                        </p>
                        <Button
                          type="button"
                          className="w-full"
                          variant="default"
                          disabled={completePending}
                          onClick={onMarkSeenClick}
                        >
                          {detail.completed ? 'Retour aux tutoriels' : 'Marquer comme vu'}
                        </Button>
                      </>
                    )}

                    {isEtapes && (
                      <div className="space-y-4">
                        {etapes[stepIndex] ? (
                          <>
                            <div className="overflow-hidden rounded-lg border border-border bg-card">
                              {currentStepScreenshot && (
                                <div className="relative max-h-[40vh] overflow-auto bg-muted/50">
                                  <img
                                    src={currentStepScreenshot}
                                    alt=""
                                    className="h-auto w-full max-w-full object-contain align-middle"
                                  />
                                  <StepPositionedBubbles etape={etapes[stepIndex]} />
                                </div>
                              )}
                              <div className="shrink-0 p-4">
                                <p className="text-xs text-muted-foreground">
                                  Étape {stepIndex + 1} / {etapes.length}
                                </p>
                                {etapes[stepIndex].texte &&
                                  (String(etapes[stepIndex].texte).includes('<') ? (
                                    <div
                                      className="mt-2 text-sm leading-relaxed text-foreground [&_a]:underline"
                                      dangerouslySetInnerHTML={{ __html: String(etapes[stepIndex].texte) }}
                                    />
                                  ) : (
                                    <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-foreground">
                                      {String(etapes[stepIndex].texte)}
                                    </p>
                                  ))}
                              </div>
                            </div>
                            {etapes.length > 0 && (
                              <footer className="grid shrink-0 grid-cols-3 items-center gap-2 border-t border-border pt-6">
                                <div className="min-w-0 justify-self-start">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="border-border bg-transparent text-foreground disabled:opacity-40"
                                    disabled={stepIndex === 0}
                                    onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
                                  >
                                    <ChevronLeft className="mr-1 h-3.5 w-3.5 shrink-0" strokeWidth={2} aria-hidden />
                                    Précédent
                                  </Button>
                                </div>
                                <div className="justify-self-center">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="border-border bg-transparent text-foreground"
                                    disabled={!hasFullViewUrl}
                                    title={
                                      hasFullViewUrl
                                        ? 'Ouvrir la vue plein écran dans un nouvel onglet'
                                        : 'URL plein écran non configurée pour ce tutoriel'
                                    }
                                    onClick={openTutorialExpandedWindow}
                                  >
                                    Agrandir
                                  </Button>
                                </div>
                                <div className="flex min-w-0 justify-end justify-self-end">
                                  {!lastStep ? (
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="default"
                                      onClick={() => setStepIndex((i) => Math.min(etapes.length - 1, i + 1))}
                                    >
                                      Suivant
                                      <ChevronRight className="ml-1 h-4 w-4 shrink-0" strokeWidth={2} aria-hidden />
                                    </Button>
                                  ) : (
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="default"
                                      disabled={completePending}
                                      className="font-semibold shadow-sm"
                                      onClick={onParcoursTermineClick}
                                    >
                                      Parcours terminé
                                    </Button>
                                  )}
                                </div>
                              </footer>
                            )}
                          </>
                        ) : (
                          <p className="text-sm text-muted-foreground">Aucune étape définie pour ce tutoriel.</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
};

export default HelpCenterPanel;
